import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  model,
  OnDestroy,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HxBaseControl } from '../../base/hx-base-control';
import { HxError, HxFormGroup, HxHint } from '../form-group/form-group';

import { HxIcon } from '../icon/icon';
import { HxInput } from '../input/input';
import { HxLabel } from '../label/label';
export type MaskPattern = 'start' | 'end';
export type UnmaskProperty = 'permanent' | 'short';
export type MaskedTextSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';

@Component({
  selector: 'hx-masked-text',
  standalone: true,
  imports: [CommonModule, FormsModule, HxFormGroup, HxLabel, HxHint, HxError, HxInput, HxIcon],
  templateUrl: './masked-text.html',
  styleUrls: ['./masked-text.css', '../../styles/form-control-tokens.css'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HxMaskedText extends HxBaseControl implements OnDestroy {
  // === Unique ID ===
  readonly idInput = input<string | undefined>(undefined);

  // === Inputs (Signals) ===
  readonly label = input.required<string>();
  readonly watermark = input<string>('');
  readonly helpText = input<string | undefined>(undefined);
  readonly size = input<MaskedTextSize>('md');

  readonly customCssClass = input<string>('');

  // Masking Configuration
  readonly maskPattern = input<MaskPattern>('start');
  readonly maskCount = input<number>(0);
  readonly maskAll = input<boolean>(true);
  readonly maskChar = input<string>('•');
  readonly unmaskProperty = input<UnmaskProperty>('short');

  // Character Allow Rules (Sanitization)
  readonly allowAlpha = input<boolean>(true);
  readonly allowNumbers = input<boolean>(true);
  readonly allowSpecial = input<boolean>(true);

  // Security Configuration
  readonly allowCopy = input<boolean>(false);

  // Validation Rules
  readonly regexExpression = input<string | RegExp | null>(null);
  readonly regexError = input<string>('Input does not match required format.');
  readonly allowRegexErrors = input<boolean>(false);

  // Unmask Duration (in seconds)
  readonly unmaskDurationSeconds = input<number>(3);
  readonly unmaskDuration = computed(() => this.unmaskDurationSeconds() * 1000);

  // === Model (Two-way Binding) ===
  readonly value = model<string>('');

  // === Internal State ===
  protected readonly isMasked = signal<boolean>(true);
  protected readonly errorState = signal<string | null>(null);
  protected readonly softWarning = signal<string | null>(null);
  protected readonly remainingTime = signal<number>(0);

  // Interaction State
  protected readonly touched = signal<boolean>(false);
  protected readonly dirty = signal<boolean>(false);

  private _timer: ReturnType<typeof setTimeout> | null = null;
  private _interval: ReturnType<typeof setInterval> | null = null;
  private _warningTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    super();
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }

    effect(() => {
      this._validateMasked(this.value());
    });
  }

  // === Base Control Overrides for CVA ===

  protected override _setValue(value: any): void {
    const safeValue = value === null || value === undefined ? '' : String(value).trim();
    this.value.set(safeValue);
    this._validateMasked(safeValue);
  }

  ngOnDestroy(): void {
    this.clearTimers();
    if (this._warningTimer) {
      clearTimeout(this._warningTimer);
    }
  }

  // === Computed Logic ===

  protected readonly hostCssClasses = computed(() => {
    const classes = ['hx-masked-text', this.customCssClass()];
    classes.push(`hx-masked-text--size-${this.size()}`);

    if (this.disabledState()) {
      classes.push('hx-masked-text--disabled');
    }
    return classes.filter(Boolean).join(' ');
  });

  protected readonly showError = computed(() => {
    return !!this.errorState();
  });

  protected readonly displayValue = computed(() => {
    const rawVal = this.value() ?? '';
    if (!this.isMasked() || !rawVal) {
      return rawVal;
    }
    return this.getMaskedString(rawVal);
  });

  protected readonly remainingTimeDisplay = computed(() => {
    const ms = this.remainingTime();
    return ms > 0 ? `${Math.ceil(ms / 1000)}s` : '';
  });

  protected readonly isTemporaryRevealActive = computed(() => {
    return !this.isMasked() && this.unmaskProperty() === 'short';
  });

  // === Actions ===

  protected onInput(event: Event): void {
    this.dirty.set(true);

    const inputEl = event.target as HTMLInputElement;
    const newDisplayValue = inputEl.value;

    if (!this.isMasked()) {
      this.updateModel(newDisplayValue, inputEl);
      return;
    }

    const oldModelValue = this.value() ?? '';
    const oldDisplayValue = this.getMaskedString(oldModelValue);

    if (newDisplayValue === oldDisplayValue) {
      return;
    }

    let start = 0;
    while (
      start < oldDisplayValue.length &&
      start < newDisplayValue.length &&
      oldDisplayValue[start] === newDisplayValue[start]
    ) {
      start++;
    }

    let oldEnd = oldDisplayValue.length;
    let newEnd = newDisplayValue.length;
    while (oldEnd > start && newEnd > start && oldDisplayValue[oldEnd - 1] === newDisplayValue[newEnd - 1]) {
      oldEnd--;
      newEnd--;
    }

    const insertedText = newDisplayValue.slice(start, newEnd);
    const prefix = oldModelValue.slice(0, start);
    const suffix = oldModelValue.slice(oldEnd);
    const reconstructedModel = prefix + insertedText + suffix;

    this.updateModel(reconstructedModel, inputEl);
  }

  protected onCopy(event: ClipboardEvent): void {
    if (!this.allowCopy()) {
      return;
    }

    const inputEl = event.target as HTMLInputElement;
    const selectionStart = inputEl.selectionStart ?? 0;
    const selectionEnd = inputEl.selectionEnd ?? 0;

    if (selectionStart === selectionEnd) {
      return;
    }

    const rawValue = this.value() ?? '';
    const copiedText = rawValue.substring(selectionStart, selectionEnd);

    if (copiedText) {
      event.clipboardData?.setData('text/plain', copiedText);
      event.preventDefault();
    }
  }

  protected onBlur(): void {
    this.touched.set(true);
    this.markTouched();
    this._validateMasked(this.value());
  }

  protected toggleMask(): void {
    if (this.unmaskProperty() === 'permanent') {
      this.isMasked.update((v) => !v);
    } else {
      this.startShortUnmask();
    }
  }

  // === Private Helpers ===

  private updateModel(rawValue: string, inputEl: HTMLInputElement): void {
    let sanitized = '';
    let strippedAlpha = false;
    let strippedNumber = false;
    let strippedSpecial = false;

    for (const char of rawValue) {
      if (this.isCharAllowed(char)) {
        sanitized += char;
      } else {
        if (/[a-zA-Z]/.test(char)) {
          strippedAlpha = true;
        } else if (/[0-9]/.test(char)) {
          strippedNumber = true;
        } else {
          strippedSpecial = true;
        }
      }
    }

    if (sanitized !== rawValue) {
      let msg = 'Invalid character ignored';
      if (strippedAlpha) {
        msg = 'Letters are not allowed';
      } else if (strippedNumber) {
        msg = 'Numbers are not allowed';
      } else if (strippedSpecial) {
        msg = 'Special characters are not allowed';
      }
      this.softWarning.set(msg);
    } else {
      this.softWarning.set(null);
    }

    this.value.set(sanitized);
    this.notifyChange(sanitized);

    const expectedDisplay = this.isMasked() ? this.getMaskedString(sanitized) : sanitized;

    if (inputEl.value !== expectedDisplay) {
      const selectionStart = inputEl.selectionStart;
      inputEl.value = expectedDisplay;

      if (selectionStart !== null) {
        inputEl.setSelectionRange(selectionStart, selectionStart);
      }
    }
  }

  private getMaskedString(val: string): string {
    const pattern = this.maskPattern();
    const count = this.maskAll() ? val.length : this.maskCount();
    const len = val.length;
    const safeCount = Math.min(count, len);
    const maskChar = this.maskChar();

    if (pattern === 'start') {
      return maskChar.repeat(safeCount) + val.slice(safeCount);
    }
    return val.slice(0, len - safeCount) + maskChar.repeat(safeCount);
  }

  private isCharAllowed(char: string): boolean {
    if (this.regexExpression()) {
      return true;
    }
    if (/[a-zA-Z]/.test(char) && !this.allowAlpha()) {
      return false;
    }
    if (/[0-9]/.test(char) && !this.allowNumbers()) {
      return false;
    }
    if (/[^a-zA-Z0-9]/.test(char) && !this.allowSpecial()) {
      return false;
    }
    return true;
  }

  private _validateMasked(val: string | null | undefined): void {
    this.errorState.set(null);
    const safeVal = val ?? '';

    if (!safeVal) {
      if (this.required() && (this.touched() || this.dirty() || this.ngControl?.touched)) {
        this.errorState.set(`${this.label()} is required.`);
      }
      return;
    }

    const regex = this.regexExpression();
    if (regex && !this.allowRegexErrors()) {
      const pattern = typeof regex === 'string' ? new RegExp(regex) : regex;
      if (!pattern.test(safeVal)) {
        this.errorState.set(this.regexError());
      }
    }
  }

  private startShortUnmask(): void {
    this.clearTimers();
    this.isMasked.set(false);
    this.remainingTime.set(this.unmaskDuration());

    this._interval = setInterval(() => {
      this.remainingTime.update((t) => Math.max(0, t - 100));
    }, 100);

    this._timer = setTimeout(() => {
      this.isMasked.set(true);
      this.clearTimers();
    }, this.unmaskDuration());
  }

  private clearTimers(): void {
    if (this._timer) {
      clearTimeout(this._timer);
    }
    if (this._interval) {
      clearInterval(this._interval);
    }
    this._timer = null;
    this._interval = null;
    this.remainingTime.set(0);
  }
}
